import React from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box, Typography } from '@mui/material';
import { ClinicalTrialsSponsorsModel } from '../../../../../models/data-sections/clinical-trials/sponsorsModel';

interface StudySponsorsProps {
    sponsors: ClinicalTrialsSponsorsModel[];
}

const StudySponsors: React.FC<StudySponsorsProps> = ({ sponsors }) => {
    if (!sponsors || sponsors.length === 0) {
        return (
            <Typography sx={{ textAlign: 'center', mt: 4 }}>
                No sponsors data available.
            </Typography>
        );
    }

    const columns: GridColDef<ClinicalTrialsSponsorsModel>[] = [
        {
            field: 'agency_class',
            headerName: 'Agency Class',
            width: 150,
            renderCell: (params) => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    {params.value || 'N/A'}
                </div>
            ),
        },
        {
            field: 'lead_or_collaborator',
            headerName: 'Role',
            width: 150,
            renderCell: (params) => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    {params.value || 'N/A'}
                </div>
            ),
        },
        {
            field: 'name',
            headerName: 'Name',
            width: 400,
            renderCell: (params) => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    {params.value || 'N/A'}
                </div>
            ),
        },
    ];

    const rows =
        sponsors?.map((sponsor, index) => ({
            id: index, // Unique row ID for DataGrid
            nct_id: sponsor.nct_id, // Include nct_id to match ClinicalTrialsSponsorsModel interface
            agency_class: sponsor.agency_class,
            lead_or_collaborator: sponsor.lead_or_collaborator,
            name: sponsor.name,
        })) || [];

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%', // Ensure the grid container takes the full height
                width: '100%',
                overflow: 'hidden', // Prevent overflow outside the grid
            }}
        >
            {rows.length > 0 ? (
                <DataGrid<ClinicalTrialsSponsorsModel>
                    rows={rows}
                    columns={columns}
                    pageSizeOptions={[5, 10, 25]}
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: 10, page: 0 },
                        },
                    }}
                    disableRowSelectionOnClick
                    autoHeight={false} // Disable shrinking of the grid to rows' height
                    sx={{
                        flex: 1, // Ensure the grid stretches to fill the container
                        display: 'flex',
                        flexDirection: 'column',
                        '& .MuiDataGrid-main': {
                            flex: 1, // Ensure the main area grows to take remaining space
                            overflow: 'auto', // Add scroll to the main content
                        },
                        '& .MuiDataGrid-footerContainer': {
                            backgroundColor: '#ffffff',
                            borderTop: '1px solid #e0e0e0',
                            position: 'sticky', // Keep the footer at the bottom
                            bottom: 0,
                            zIndex: 1, // Ensure the footer is above other elements
                        },
                    }}
                />
            ) : (
                <Typography sx={{ textAlign: 'center', mt: 4 }}>
                    No sponsor data available.
                </Typography>
            )}
        </Box>
    );
};

export default StudySponsors;
